import { LatLngToWorldPoint } from '../../../modules/LatLngToWorldPoint.js';
import { WorldPointToPixelPoint } from '../../../modules/WorldPointToPixelPoint.js';

/**
 * Web maps load imagery as a grid of 256×256 pixel images called tiles. At zoom level N the world
 * is a 2^N × 2^N grid — each tile has a column and row address with (0,0) at the northwest corner.
 * This overlay fetches tiles at a fixed high zoom level (TILE_ZOOM) to show sharper imagery than
 * the map's default tiles.
 */

/**
 * @typedef {'satellite'|'roadmap'|'terrain'|'hybrid'|'aerial'} MapBase
 */

/** Zoom level for tile imagery.  At this zoom level, these tiles cover a very small area */
const TILE_ZOOM = 20;

/** All Google map tiles are 256×256 pixels regardless of zoom level */
const TILE_SIZE = 256;

/** The urls for each map base type */
const TILE_URLS_BY_MAP_BASE = {
  satellite: 'https://mt0.google.com/vt/lyrs=s&hl=en&x=',
  roadmap: 'https://mt0.google.com/vt/lyrs=m&hl=en&x=',
  terrain: 'https://mt0.google.com/vt/lyrs=p&hl=en&x=',
  hybrid: 'https://mt0.google.com/vt/lyrs=y&hl=en&x=',
  aerial: 'https://khms0.googleapis.com/kh?v=871&hl=en-US&x='
};

/**
 * Builds the url to grab the tile with the given configuration from Google
 * @param {MapBase} mapBase The map base to use for tile imagery
 * @param {number} tileColumn Column index in the tile grid (increases eastward)
 * @param {number} tileRow Row index in the tile grid (increases southward)
 * @returns {string} The full tile image URL
 */
function buildTileUrl(mapBase, tileColumn, tileRow) {
  // Fall back to aerial if the provided mapBase does not match a known prefix
  const urlPrefix = TILE_URLS_BY_MAP_BASE[mapBase] ?? TILE_URLS_BY_MAP_BASE.aerial;
  return `${urlPrefix}${tileColumn}&y=${tileRow}&z=${TILE_ZOOM}#superzoom`;
}

/**
 * Returns the lat/lng of a tile's northwest corner based on its grid address and zoom level.
 * @param {number} tileColumn Column index in the tile grid
 * @param {number} tileRow Row index in the tile grid
 * @param {number} zoomLevel Determines how many tiles span the world (2^zoomLevel per side)
 * @returns {google.maps.LatLng} The lat/lng of the tile's northwest corner
 */
function tileNorthwestCorner(tileColumn, tileRow, zoomLevel) {
  const tileCount = Math.pow(2, zoomLevel);
  // Each tile spans 360/2^N degrees of longitude
  const longitude = (tileColumn / tileCount) * 360 - 180;
  // Inverse Web Mercator formula for latitude
  const latitudeRadians = Math.atan(Math.sinh(Math.PI * (1 - (2 * tileRow) / tileCount)));
  const latitude = latitudeRadians * (180 / Math.PI);
  return new google.maps.LatLng(latitude, longitude);
}

/**
 * Returns the grid address (column, row) of the tile that contains the given lat/lng.
 * @param {google.maps.LatLng} mapCoordinate The geographic coordinate to convert
 * @param {number} zoomLevel The zoom level that determines tile grid resolution
 * @returns {{ tileColumn: number, tileRow: number }} Grid address of the tile containing the point
 */
function mapCoordinateToTileGridAddress(mapCoordinate, zoomLevel) {
  const worldPoint = LatLngToWorldPoint(mapCoordinate);
  const pixelPoint = WorldPointToPixelPoint(worldPoint, zoomLevel);
  return {
    tileColumn: Math.floor(pixelPoint.x / TILE_SIZE),
    tileRow: Math.floor(pixelPoint.y / TILE_SIZE)
  };
}

/**
 * A Google Maps OverlayView that renders high zoom level tiles in a grid anchored to the map center
 * @class
 * @name SuperZoomOverlay
 * @extends {google.maps.OverlayView}
 * @param {MapBase} mapBase - The string key representing the map base style
 */

/**
 * Creates and returns a SuperZoomOverlay instance that renders high resolution map tiles.
 * @param {MapBase} mapBase The map base style to use for tile imagery
 * @returns {SuperZoomOverlay} A new SuperZoomOverlay instance
 */
export function createSuperZoomOverlay(mapBase) {
  // The class needs to be defined inside the function because google.maps is not available when the file is imported
  const CachedOverlayClass = class SuperZoomOverlay extends google.maps.OverlayView {
    #containerElement = null;
    // Keyed by "column,row" string so existing img elements can be reused across draw calls
    #tileImageElements = new Map();
    #mapBase;

    /**
     * Initializes the SuperZoomOverlay with the provided map base style.
     * @param {string} mapBase The map base style key used to select tile imagery
     */
    constructor(mapBase) {
      super();
      // Store the map base so tile URLs can be built correctly during draw calls
      this.#mapBase = mapBase;
    }

    /**
     * Updates the map base style and redraws all tiles to reflect the change.
     * Has no effect if the new map base is the same as the current one.
     * @param {string} mapBase The new map base style key to switch to
     */
    setMapBase(mapBase) {
      // Only set the map base if it's changed
      if (this.#mapBase === mapBase) return;
      this.#mapBase = mapBase;

      // Remove all old tiles and get new ones with the updated map base
      this.#clearTiles();
      if (this.getMap()) this.draw();
    }

    /**
     * Called by the Maps API when the overlay is added to a map.
     * Creates the container div and attaches it to the lowest map pane so tiles render beneath all other layers.
     */
    onAdd() {
      // Create an absolutely positioned div to serve as the parent for all tile images
      this.#containerElement = document.createElement('div');
      Object.assign(this.#containerElement.style, {
        position: 'absolute',
        top: '0',
        left: '0',
        // Prevent tile images from intercepting mouse or touch events on the map
        pointerEvents: 'none'
      });
      // Append to mapPane so tiles render beneath markers, polygons, and other overlays
      this.getPanes().mapPane.appendChild(this.#containerElement);
    }

    /**
     * Called by the Maps API whenever the map is panned or zoomed.
     * Anchors the tile grid to the center tile, then expands outward in all directions
     * until the viewport is fully covered.
     */
    draw() {
      // Return early if the overlay is not yet fully initialized
      const map = this.getMap();
      const projection = this.getProjection();
      if (!map || !projection || !this.#containerElement) return;

      // Find the tile at the map center — all other tiles are positioned relative to this one
      const mapCenter = map.getCenter();
      const { tileColumn: centerTileColumn, tileRow: centerTileRow } = mapCoordinateToTileGridAddress(mapCenter, TILE_ZOOM);

      // Get the height and width of tiles based on the current zoom level
      // This happens by subtracting the x and y positions of the center tile and the diagonally adjacent tile
      const centerTileNWPixelPosition = projection.fromLatLngToDivPixel(tileNorthwestCorner(centerTileColumn, centerTileRow, TILE_ZOOM));
      const adjacentTileNWPixel = projection.fromLatLngToDivPixel(tileNorthwestCorner(centerTileColumn + 1, centerTileRow + 1, TILE_ZOOM));
      const tileWidthInPixels = adjacentTileNWPixel.x - centerTileNWPixelPosition.x;
      const tileHeightInPixels = adjacentTileNWPixel.y - centerTileNWPixelPosition.y;

      // Calculate how many tiles to fully cover the viewport
      const mapDivElement = map.getDiv();
      const edgeBufferInTiles = 1; // Add extra tiles on all sides to cover all edges
      const tilesFromCenterToHorizontalEdge = Math.max(Math.ceil(mapDivElement.offsetWidth / tileWidthInPixels) + edgeBufferInTiles, 1);
      const tilesFromCenterToVerticalEdge = Math.max(Math.ceil(mapDivElement.offsetHeight / tileHeightInPixels) + edgeBufferInTiles, 1);

      // Package all the tile grid parameters into objects
      const centerTileAddress = { column: centerTileColumn, row: centerTileRow };
      const tileDimensionsInPixels = { width: tileWidthInPixels, height: tileHeightInPixels };
      const tilesFromCenterToEdges = { columns: tilesFromCenterToHorizontalEdge, rows: tilesFromCenterToVerticalEdge };

      // Render and position all tiles in the grid that makes up the viewport, then remove any tiles that are offscreen
      const neededTileKeys = this.#updateTileGrid(
        centerTileAddress,
        centerTileNWPixelPosition,
        tileDimensionsInPixels,
        tilesFromCenterToEdges
      );
      this.#pruneOffscreenTiles(neededTileKeys);
    }

    /**
     * Renders and positions every tile in the grid that covers the viewport.
     * Returns the set of tile keys that are needed this frame.
     * @param {{ column: number, row: number }} centerTileAddress - The grid address of the center tile
     * @param {google.maps.Point} centerTileNWPixelPosition - The pixel position of the center tile's northwest corner
     * @param {{ width: number, height: number }} tileDimensionsInPixels - The pixel dimensions of a single tile
     * @param {{ columns: number, rows: number }} tilesFromCenterToEdges - The number of tiles from center to each edge
     * @returns {Set<string>}
     */
    #updateTileGrid(centerTileAddress, centerTileNWPixelPosition, tileDimensionsInPixels, tilesFromCenterToEdges) {
      // Create a set to store all of the keys of the tiles that are in the view
      const tileKeysInView = new Set();

      // Get the necessary parameters for calculating tile positions and addresses from the provided objects
      const { column: centerTileColumn, row: centerTileRow } = centerTileAddress;
      const { width: tileWidthInPixels, height: tileHeightInPixels } = tileDimensionsInPixels;
      const { columns: tilesFromCenterToHorizontalEdge, rows: tilesFromCenterToVerticalEdge } = tilesFromCenterToEdges;

      // Set the loop starting positions
      const startingColumn = -tilesFromCenterToHorizontalEdge; // Start at the leftmost column
      const startingRow = -tilesFromCenterToVerticalEdge; // Start at the topmost row (y decreases as you go up in pixel coordinates)

      // Loop over every tile in the grid that makes up the viewport, fetch each tile's image, and position it properly within the container
      for (
        let columnOffsetFromCenter = startingColumn;
        columnOffsetFromCenter <= tilesFromCenterToHorizontalEdge;
        columnOffsetFromCenter++
      ) {
        for (let rowOffsetFromCenter = startingRow; rowOffsetFromCenter <= tilesFromCenterToVerticalEdge; rowOffsetFromCenter++) {
          // Calculate the current tile's column and row by moving outward from the center tile by the current offsets
          const tileColumn = centerTileColumn + columnOffsetFromCenter;
          const tileRow = centerTileRow + rowOffsetFromCenter;
          const currentTileKey = `${tileColumn},${tileRow}`;
          tileKeysInView.add(currentTileKey);

          // Create a new img element only if this tile has not been loaded yet
          const existingTileElement = this.#tileImageElements.get(currentTileKey);
          const tileImageElement = existingTileElement ?? this.#createTileElement(tileColumn, tileRow);

          // Position the tile at its pixel offset from the center tile's screen position
          tileImageElement.style.left = `${Math.round(centerTileNWPixelPosition.x + columnOffsetFromCenter * tileWidthInPixels)}px`;
          tileImageElement.style.top = `${Math.round(centerTileNWPixelPosition.y + rowOffsetFromCenter * tileHeightInPixels)}px`;
          tileImageElement.style.width = `${Math.round(tileWidthInPixels)}px`;
          tileImageElement.style.height = `${Math.round(tileHeightInPixels)}px`;
        }
      }

      return tileKeysInView;
    }

    /**
     * Removes any tile elements that are no longer within the visible viewport.
     * @param {Set<string>} neededTileKeys The set of tile keys that should be kept
     */
    #pruneOffscreenTiles(neededTileKeys) {
      for (const [tileKey, tileImageElement] of this.#tileImageElements) {
        if (!neededTileKeys.has(tileKey)) {
          tileImageElement.remove();
          this.#tileImageElements.delete(tileKey);
        }
      }
    }

    /**
     * Called by the Maps API when the overlay is removed from the map via setMap(null).
     * Removes all tile images and the container element from the DOM.
     */
    onRemove() {
      // Destroy all tile img elements before detaching the container itself
      this.#clearTiles();
      this.#containerElement?.remove();
      this.#containerElement = null;
    }

    /**
     * Removes all cached tile image elements from the DOM and empties the tile registry.
     */
    #clearTiles() {
      // Detach every tile img element from the container in one pass
      for (const tileImageElement of this.#tileImageElements.values()) tileImageElement.remove();
      this.#tileImageElements.clear();
    }

    /**
     * Creates a new img element for the tile at the given grid address, appends it to the container, and stores it in the registry.
     * @param {number} tileColumn - Column index in the tile grid
     * @param {number} tileRow - Row index in the tile grid
     * @return {HTMLImageElement} The newly created img element for the tile
     */
    #createTileElement(tileColumn, tileRow) {
      // Create a new img element for the tile at the given grid address
      const tileImageElement = document.createElement('img');
      tileImageElement.style.position = 'absolute';
      // Explicitly prevent touch/pointer interception on mobile — inheritance of pointer-events
      // from the container is unreliable on iOS Safari, and img elements have native drag behavior
      tileImageElement.style.pointerEvents = 'none';
      tileImageElement.draggable = false;
      tileImageElement.src = buildTileUrl(this.#mapBase, tileColumn, tileRow);

      // Append the new tile element to the container and store it in the registry
      this.#containerElement.appendChild(tileImageElement);

      // Store the tile element in the registry keyed by its grid address for easy retrieval in future draw calls
      const tileKey = `${tileColumn},${tileRow}`;
      this.#tileImageElements.set(tileKey, tileImageElement);

      return tileImageElement;
    }
  };

  return new CachedOverlayClass(mapBase);
}
